import { ParsedUrlQuery } from 'querystring'
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    InputBase,
    AutocompleteRenderOptionState,
    InputAdornment,
    Popper,
    Typography,
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import { useTheme } from '@mui/material/styles'
import { AutocompleteCloseReason } from '@mui/material/useAutocomplete'
import { C1Button, C1ButtonColorType } from 'c1ui'
import clsx from 'clsx'
import { C1ButtonDropdownEndIcon } from 'components/core/button'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CancelFunc } from 'lib/cancel'
import { Search } from 'lucide-react'
import { useRouter } from 'next/router'
import React, { ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from './styles'

export type onChangeFunc<T> = (selected: T, loading: boolean) => void
export type fromQueryFunc<T> = (query: ParsedUrlQuery) => T
export type toQueryFunc<T> = (query: ParsedUrlQuery, selected: T | null) => void

type filterProps<T> = {
    label: string
    options: T[]
    selected?: T | null

    fromQuery?: fromQueryFunc<T>
    toQuery?: toQueryFunc<T>
    onChange: onChangeFunc<T | undefined>
    search?: (query: string, exclude: T | null) => CancelFunc
    doFetch?: (options: T) => CancelFunc

    renderLabel: (option: T) => string
    renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactElement
    widePopper?: boolean
    onOpen?: () => void

    clearOnBlur?: boolean
    clearOnClick?: boolean
    loading?: boolean
    error?: boolean
    noOptionsText?: React.ReactNode
    disabled?: boolean
    errorText?: React.ReactNode
    loadingText?: React.ReactNode
}

export const SingleSelectFilter = <T, >({
    label,
    options,
    selected,
    fromQuery,
    toQuery,
    onChange,
    search,
    doFetch,
    renderLabel,
    renderOption,
    widePopper = false,
    onOpen,
    loading = undefined,
    noOptionsText = '(No options)',
    disabled = false,
    error,
    errorText,
    loadingText,
    clearOnBlur,
    clearOnClick = true, // backwards compatability
}: filterProps<T>): React.ReactElement => {
    const router = useRouter()
    const theme = useTheme()
    const classes = useStyles(theme)

    const searchCancelRef = useRef<CancelFunc>(() => undefined)
    const fetchCancelRef = useRef<CancelFunc>(() => undefined)

    const [loaded, setLoaded] = useState<boolean>(false)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const [inputValue, setInputValue] = useState('')
    const [pendingValue, setPendingValue] = React.useState<T | null>(null)

    // Url query changes clears the pending value
    useEffect((): void => {
        if (!selected) {
            setPendingValue(null)
        }
    }, [selected])

    // Cancel any in-flight searches on unmount
    useEffect((): (() => void) => {
        return (): void => {
            searchCancelRef.current()
            fetchCancelRef.current()
        }
    }, [])

    // Load initial state from query and fire off a fetch.
    useEffect(() => {
        if (loaded || !fromQuery) {
            return
        }
        setLoaded(true)

        const selectedFromQuery = fromQuery(router.query)
        setPendingValue(selectedFromQuery)
        if (doFetch && selectedFromQuery) {
            fetchCancelRef.current()
            fetchCancelRef.current = doFetch(selectedFromQuery)
        }
        onChange(selectedFromQuery, true)
    }, [router.query, loaded, fromQuery, onChange, doFetch])

    // If our current selection is provided by the filter, we need to listen to it.
    useEffect(() => {
        if (selected) {
            setPendingValue(selected)
        }
    }, [selected])

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget)
        if (search && clearOnClick) {
            searchCancelRef.current()
            searchCancelRef.current = search('', pendingValue)
        }
    }

    const handleClose = (_event: React.ChangeEvent<unknown>, reason: AutocompleteCloseReason): void => {
        if (reason === 'toggleInput') {
            return
        }
        if (anchorEl) {
            anchorEl.focus()
        }

        setAnchorEl(null)
        toQuery && toQuery(router.query, pendingValue)

        if (pendingValue) {
            onChange(pendingValue, false)
        } else {
            onChange(undefined, false)
        }
    }

    const open = Boolean(anchorEl)
    const id = open ? 'search-filter' : undefined

    const handleInputChange = useCallback(
        (event: SyntheticEvent, newInputValue): void => {
            if (event && event.type === 'change') {
                setInputValue(newInputValue)
                if (search) {
                    searchCancelRef.current()
                    searchCancelRef.current = search(newInputValue, pendingValue)
                }
            }
        },
        [search, pendingValue],
    )

    const handleChange = (event: SyntheticEvent, option: null | T): void => {
        if (!option) {
            option = null
        }
        setPendingValue(option)
    }

    const handleSelect = useCallback((option: T) => {
        if (option === pendingValue) {
            setPendingValue(null)
        } else {
            setPendingValue(option)
        }
        setInputValue('')
    }, [pendingValue])

    // Need to define this to avoid a warning
    const handleKeyPress = useCallback(() => {
        return null
    }, [])

    const renderCheckboxOption = useCallback(
        (
            props: React.HTMLAttributes<HTMLLIElement> & React.Attributes,
            option: T,
            optionState: AutocompleteRenderOptionState,
        ): React.ReactElement => (
            <li
                {...props}
                className={clsx(classes.item, props.className)}
                role="option"
                aria-selected={optionState.selected}
                onClick={(): void => handleSelect(option)}
                onKeyPress={handleKeyPress}
            >
                <Checkbox
                    color="primary"
                    size="small"
                    checked={optionState.selected}
                />
                {renderOption ? (
                    renderOption(props, option)
                ) : (
                    <Typography>{renderLabel(option)}</Typography>
                )}
            </li>
        ),
        [renderOption, renderLabel, classes.item, handleSelect, handleKeyPress],
    )

    const buttonLabel = useMemo(() => {
        let labelContent = label
        if (pendingValue) {
            labelContent += `: ${renderLabel(pendingValue)}`
        }
        return labelContent
    }, [pendingValue, label, renderLabel])

    useEffect(() => {
        open && onOpen?.()
    }, [open, onOpen])

    const renderInput: React.FC<AutocompleteRenderInputParams> = useCallback(
        ({ inputProps, InputProps }): React.ReactElement => (
            <InputBase
                ref={InputProps.ref}
                inputProps={inputProps}
                autoFocus
                className={classes.inputBase}
                placeholder="Search"
                endAdornment={
                    <InputAdornment position="end">
                        <C1Icon icon={Search} size="sm" />
                    </InputAdornment>
                }
            />
        ),
        [classes.inputBase],
    )

    const nonErrorColor: C1ButtonColorType = pendingValue ? 'primary' : 'info'

    return (
        <>
            <div className={classes.root}>
                <C1Button
                    variant="outlined"
                    color={error ? 'error' : nonErrorColor}
                    onClick={handleClick}
                    aria-describedby={id}
                    endIcon={<C1ButtonDropdownEndIcon />}
                >
                    {buttonLabel}
                </C1Button>
            </div>
            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                className={clsx(classes.popper, { [classes.widePopper]: widePopper })}
                disablePortal
            >
                <Autocomplete
                    open
                    clearOnBlur={clearOnBlur}
                    disabled={disabled}
                    onClose={handleClose}
                    multiple={false}
                    classes={{
                        paper: classes.paper,
                        option: classes.option,
                        popperDisablePortal: classes.popperDisablePortal,
                    }}
                    value={pendingValue}
                    onChange={handleChange}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    disableCloseOnSelect
                    renderTags={(): ReactNode => null}
                    loading={loading}
                    noOptionsText={error ? errorText : noOptionsText}
                    loadingText={loadingText}
                    renderOption={renderCheckboxOption}
                    options={error ? [] : [...options]}
                    getOptionLabel={renderLabel}
                    renderInput={renderInput}
                />
            </Popper>
        </>
    )
}
